import { useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { FormFieldMappingForm } from 'app/components/mapping/FormFieldMappingForm'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { defaultFormFieldMappings, formFieldOptions } from 'lib/formFieldDefaults'
import { logError } from 'lib/logging'
import { useFormFieldMappingStore, useUnmappedFormKeyStore } from 'app/stores/StoreContext'

export type FormFieldMappingRow = {
  id?: string
  rawKey: string
  formField: string
  isDefault?: boolean
}

export type UnmappedFormKeyRow = {
  id: string
  rawKey: string
  vendors?: Record<string, unknown>
}

export const getFormFieldLabel = (formField: string): string =>
  formFieldOptions.find((o) => o.value === formField)?.label || formField

/** Total observed submissions for an unmapped key, summed across vendors. */
export const getUnmappedFrequency = (row: UnmappedFormKeyRow): number =>
  Object.values(row.vendors ?? {}).reduce<number>((sum, n) => sum + (Number(n) || 0), 0)

export type UseFormFieldMappingReturn = {
  /** Observed-but-unmapped raw keys, highest frequency first. */
  unmappedRows: UnmappedFormKeyRow[]
  /** Workspace-defined mappings. */
  customMappings: FormFieldMappingRow[]
  /** Built-in read-only mappings. */
  defaultMappings: FormFieldMappingRow[]
  canEditMapping: boolean
  onMapField: (unmappedId: string, rawKey: string, formField: string) => void
  onRemapField: (mapping: FormFieldMappingRow, formField: string) => void
  onEditMapping: (mapping: FormFieldMappingRow) => void
  onDeleteMapping: (id: string) => void
  onNewMapping: () => void
}

export function useFormFieldMapping(): UseFormFieldMappingReturn {
  const [confirm] = useConfirmation()
  const checkPermission = useCheckPermission()
  const canEditMapping = checkPermission([`org:settings:edit-mapping`, `settings:mapping:edit`])
  const formFieldMappingStore = useFormFieldMappingStore()
  const unmappedFormKeyStore = useUnmappedFormKeyStore()
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set())
  const [mappedUnmappedIds, setMappedUnmappedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    formFieldMappingStore.fetchList()
    unmappedFormKeyStore.fetchList()
  }, [])

  const [searchParams] = useSearchParams()
  const query = searchParams.get(`query`)?.toLowerCase()

  const matchesQuery = (m: FormFieldMappingRow) =>
    !query ||
    m.rawKey.toLowerCase().includes(query) ||
    m.formField.toLowerCase().includes(query) ||
    getFormFieldLabel(m.formField).toLowerCase().includes(query)

  const customMappings = useMemo(
    () =>
      formFieldMappingStore.hits
        .filter((m) => !deletedIds.has(m.id))
        .map((m) => ({ id: m.id, rawKey: m.rawKey, formField: m.formField, isDefault: false }))
        .filter(matchesQuery),
    [formFieldMappingStore.hits, deletedIds, query],
  )

  const defaultMappings = useMemo(
    () => defaultFormFieldMappings.map((m) => ({ ...m, isDefault: true })).filter(matchesQuery),
    [query],
  )

  const unmappedRows = useMemo(() => {
    const hits: UnmappedFormKeyRow[] = unmappedFormKeyStore.hits
    const mappedRawKeys = new Set(
      [...formFieldMappingStore.hits.filter((m) => !deletedIds.has(m.id)), ...defaultFormFieldMappings].map((m) =>
        m.rawKey.toLowerCase(),
      ),
    )
    return hits
      .filter((m) => !mappedRawKeys.has(m.rawKey.toLowerCase()) && !mappedUnmappedIds.has(m.id))
      .filter((m) => !query || m.rawKey.toLowerCase().includes(query))
      .slice()
      .sort((a, b) => getUnmappedFrequency(b) - getUnmappedFrequency(a))
  }, [query, unmappedFormKeyStore.hits, formFieldMappingStore.hits, deletedIds, mappedUnmappedIds])

  const refreshLists = () => {
    formFieldMappingStore.fetchList({}, { requestPolicy: 'network-only' })
    unmappedFormKeyStore.fetchList({}, { requestPolicy: 'network-only' })
  }

  const onNewMapping = () =>
    showModal(<FormFieldMappingForm isDefault={false} onSuccess={refreshLists} />, {
      title: `Create New Form Field Mapping`,
      isFullScreen: false,
      removePadding: true,
      useRadix: true,
    })

  const onEditMapping = (mapping: FormFieldMappingRow) =>
    showModal(<FormFieldMappingForm mapping={mapping} isDefault={false} onSuccess={refreshLists} />, {
      title: `Edit Form Field Mapping`,
      isFullScreen: false,
      removePadding: true,
      useRadix: true,
    })

  const onDeleteMapping = async (id: string) => {
    await confirm({
      title: `Delete Form Field Mapping`,
      description: `Are you sure you want to delete this form field mapping?`,
      variant: `danger`,
    }).then(async () => {
      setDeletedIds((prev) => new Set([...prev, id]))

      const result = await formFieldMappingStore.remove(id)

      if (result.error) {
        setDeletedIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
        logError(`Error deleting form field mapping`, result.error)
        notifyError(`Error deleting form field mapping`)
      } else {
        notifySuccess(`Deleted Successfully`)
        setMappedUnmappedIds(new Set())
        refreshLists()
      }
    })
  }

  const onMapField = async (unmappedId: string, rawKey: string, formField: string) => {
    setMappedUnmappedIds((prev) => new Set([...prev, unmappedId]))

    const result = await formFieldMappingStore.create({ rawKey, formField })

    if (result.error) {
      setMappedUnmappedIds((prev) => {
        const next = new Set(prev)
        next.delete(unmappedId)
        return next
      })
      logError(`Error creating mapping`, result.error)
      notifyError(`Error creating mapping`)
      return
    }

    notifySuccess(`Mapping created`)
  }

  const onRemapField = async (mapping: FormFieldMappingRow, formField: string) => {
    if (!mapping.id || formField === mapping.formField) return

    const result = await formFieldMappingStore.update(mapping.id, { rawKey: mapping.rawKey, formField })

    if (result.error) {
      logError(`Error updating mapping`, result.error)
      notifyError(`Error updating mapping`)
      return
    }

    notifySuccess(`Mapping updated`)
  }

  return {
    unmappedRows,
    customMappings,
    defaultMappings,
    canEditMapping,
    onMapField,
    onRemapField,
    onEditMapping,
    onDeleteMapping,
    onNewMapping,
  }
}
